'use client'

/**
 * PaginationControls Component
 * Unified pagination UI with page navigation and size selector
 */

import * as React from 'react'
import { useTranslation } from '@ufc/core'
import { cn } from '../../lib/utils'
import { Button } from './button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from './pagination'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export interface PaginationControlsLabels {
  /** "Showing X-Y of Z" format - use {{start}}, {{end}}, {{total}} placeholders */
  showing?: string
  /** "Per page" label */
  perPage?: string
  /** Aria label for page number - use {{page}} placeholder */
  goToPage?: string
  /** Aria label for previous button */
  previousPage?: string
  /** Aria label for next button */
  nextPage?: string
}

export interface PaginationControlsProps {
  /** Current page (1-indexed) */
  page: number
  /** Current page size */
  pageSize: number
  /** Total number of items */
  totalItems: number
  /** Total number of pages */
  totalPages: number
  /** Available page sizes */
  pageSizes?: number[]
  /** Callback when page changes */
  onPageChange: (page: number) => void
  /** Callback when page size changes */
  onPageSizeChange: (pageSize: number) => void
  /** Show page size selector */
  showPageSizeSelector?: boolean
  /** Show item count */
  showItemCount?: boolean
  /** Is RTL mode */
  isRTL?: boolean
  /** Additional className */
  className?: string
  /** Translation labels */
  labels?: PaginationControlsLabels
}

// External helper functions
function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number = 5
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }

  const pages: (number | 'ellipsis')[] = []
  const halfVisible = Math.floor(maxVisible / 2)

  // Always show first page
  pages.push(1)

  // Calculate start and end of visible range
  let start = Math.max(2, currentPage - halfVisible)
  let end = Math.min(totalPages - 1, currentPage + halfVisible)

  // Adjust if near start or end
  if (currentPage <= halfVisible + 1) {
    end = maxVisible - 1
  } else if (currentPage >= totalPages - halfVisible) {
    start = totalPages - maxVisible + 2
  }

  // Add ellipsis and pages
  if (start > 2) pages.push('ellipsis')
  for (let i = start; i <= end; i++) pages.push(i)
  if (end < totalPages - 1) pages.push('ellipsis')

  // Always show last page
  if (totalPages > 1) pages.push(totalPages)

  return pages
}

function calculateItemRange(
  page: number,
  pageSize: number,
  totalItems: number
): { start: number; end: number } {
  const start = Math.min((page - 1) * pageSize + 1, totalItems)
  const end = Math.min(page * pageSize, totalItems)
  return { start, end }
}

// Helper to interpolate template strings
function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => String(values[key] ?? ''))
}

export function PaginationControls({
  page,
  pageSize,
  totalItems,
  totalPages,
  pageSizes = [10, 20, 50, 100],
  onPageChange,
  onPageSizeChange,
  showPageSizeSelector = true,
  showItemCount = true,
  isRTL = false,
  className,
  labels = {},
}: PaginationControlsProps) {
  const { t } = useTranslation()
  const pageNumbers = generatePageNumbers(page, totalPages)
  const { start, end } = calculateItemRange(page, pageSize, totalItems)

  const showingText = labels.showing
    ? interpolate(labels.showing, { start, end, total: totalItems })
    : t('common.pagination.showing', {
        start,
        end,
        total: totalItems,
        defaultValue: 'Showing {{start}}-{{end}} of {{total}}',
      })

  const perPageText = labels.perPage ?? t('common.pagination.perPage', { defaultValue: 'Per page' })

  const previousPageLabel = labels.previousPage ?? t('common.pagination.previousPage', { defaultValue: 'Previous page' })
  const nextPageLabel = labels.nextPage ?? t('common.pagination.nextPage', { defaultValue: 'Next page' })

  function getGoToPageAriaLabel(targetPage: number): string {
    if (labels.goToPage) return interpolate(labels.goToPage, { page: targetPage })
    return t('common.pagination.goToPage', { page: targetPage, defaultValue: 'Go to page {{page}}' })
  }

  const hasNextPage = page < totalPages
  const hasPreviousPage = page > 1

  // Don't render if no items
  if (totalItems === 0) {
    return null
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        className
      )}
    >
      {/* Item count */}
      {showItemCount && (
        <div className="w-full text-sm text-muted-foreground whitespace-nowrap sm:w-auto">
          {showingText}
        </div>
      )}

      {/* Page navigation */}
      <div className="flex w-full justify-center sm:w-auto">
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPageChange(page - 1)}
                disabled={!hasPreviousPage}
                className="h-9 w-9"
                aria-label={previousPageLabel}
              >
                {isRTL ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
            </PaginationItem>

            {pageNumbers.map((pageNum, idx) =>
              pageNum === 'ellipsis' ? (
                <PaginationItem key={`ellipsis-${idx}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={pageNum}>
                  <Button
                    variant={page === pageNum ? 'default' : 'ghost'}
                    size="icon"
                    onClick={() => onPageChange(pageNum)}
                    className="h-9 w-9"
                    aria-label={getGoToPageAriaLabel(pageNum)}
                    aria-current={page === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </Button>
                </PaginationItem>
              )
            )}

            <PaginationItem>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPageChange(page + 1)}
                disabled={!hasNextPage}
                className="h-9 w-9"
                aria-label={nextPageLabel}
              >
                {isRTL ? (
                  <ChevronLeft className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      {/* Page size selector */}
      {showPageSizeSelector && (
        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {perPageText}:
          </span>
          <Select
            value={String(pageSize)}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-9 w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizes.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  )
}
